import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card, Alert, InputGroup } from 'react-bootstrap';
import logo from '../logo.png';
import { LOGIN_SCHEMA } from '../constants/login';
import { signIn, googleLogin } from '../services/baseService';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import GoogleButton from 'react-google-button';
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function Login() {
  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validated, setValidated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  // Real-time validation
  useEffect(() => {
    const newErrors = {};

    // Username validation
    if (touched.username) {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      }
    }

    // Password validation
    if (touched.password) {
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      }
    }

    setErrors(prev => ({ ...prev, ...newErrors, main: prev.main }));
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: fieldValue }));

    // Clear specific field errors on change
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear main error when user starts typing
    if (errors.main && (name === 'username' || name === 'password')) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.main;
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched for validation display
    setTouched({ username: true, password: true });
    setValidated(true);

    const values = {
      username: formData.username,
      password: formData.password
    };

    if (localStorage.getItem('preface_admin_fcm_token') !== null) {
      values['fcm_token'] = localStorage.getItem('preface_admin_fcm_token');
    }

    try {
      const data = await LOGIN_SCHEMA.validate(values, { abortEarly: false });
      if (data) {
        const response = await signIn(data);
        if (response.status) {
          navigate('/');
        } else {
          setErrors({ main: response.message });
        }
      }
    } catch (err) {
      const errData = {};
      if (err.inner) {
        err.inner.forEach(e => {
          errData[e.path] = e.message;
        });
      } else {
        errData.main = 'An unexpected error occurred';
      }
      setErrors(errData);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      console.log(tokenResponse);
      try {
        const response = await googleLogin({ access_token: tokenResponse.access_token });
        if (response.status) {
          navigate('/');
        } else {
          setErrors({ main: response.message });
        }
      } catch (error) {
        setErrors({ main: 'Google login failed. Please try again.' });
      }
    },
    onError: () => {
      setErrors({ main: 'Google login failed. Please try again.' });
    }
  });

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleForgotPassword = () => {
    navigate('/reset-password', {
      state: { username: formData.username }
    });
  };

  return (
    <section className="login-content">
      <Container fluid className="h-100">
        <Row className="justify-content-center align-items-center height-self-center">
          <Col md={5} sm={12} className="align-self-center">
            <Card className="sign-user_card">
              <Card.Body>
                <div className="text-center mb-4">
                  <img src={logo} className="img-fluid rounded-normal light-logo logo" alt="logo" />
                </div>

                <h3 className="mb-3 text-center">Sign In</h3>
                <p className="text-center mb-4">Sign in to stay connected.</p>

                {errors.main && (
                  <Alert
                    variant="danger"
                    className="mb-3"
                    dismissible
                    onClose={() => setErrors(prev => ({ ...prev, main: null }))}
                  >
                    {errors.main}
                  </Alert>
                )}

                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.username && errors.username}
                      isValid={touched.username && !errors.username && formData.username.trim().length >= 3}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && errors.password}
                        isValid={touched.password && !errors.password && formData.password.length >= 6}
                        required
                      />
                      <InputGroup.Text
                        style={{ cursor: 'pointer' }}
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <Row className="mb-3">
                    {/* <Col xs={6}>
                      <Form.Check
                        type="checkbox"
                        id="rememberMe"
                        name="rememberMe"
                        label="Remember Me"
                        className='text-danger'
                        checked={formData.rememberMe}
                        onChange={handleChange}
                      />
                    </Col> */}
                    <Col xs={12} className="text-end">
                      <Button variant="link" onClick={handleForgotPassword} className="text-primary p-0">
                        Forgot Password?
                      </Button>
                    </Col>
                  </Row>

                  <div className="d-flex  justify-content-center mb-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="fw-bold"
                      style={{ width: "100px" }}
                      disabled={
                        // Object.keys(errors).some(key => key !== 'main' && errors[key]) || 
                        !formData.username.trim() ||
                        !formData.password.trim()
                      }
                    >
                      Sign In
                    </Button>
                  </div>

                  <div className="d-flex justify-content-center">
                    <GoogleButton onClick={handleGoogleLogin} />
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}